import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  Card, 
  CardContent, 
  CardHeader,
  Checkbox, 
  FormControlLabel,
  Link,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { FileUp, ExternalLink } from 'lucide-react';
import { API_URL } from '../../../utils/apiConfig';

const FoodHandlersCertForm = ({ employeeId, formId, onSubmit, isCompleted: initialIsCompleted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingCert, setExistingCert] = useState(null);
  const [isCompletedState, setIsCompletedState] = useState(initialIsCompleted);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [certificationChecked, setCertificationChecked] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  
  const token = localStorage.getItem('token');
  
  // Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      if (!['application/pdf', 'image/jpeg', 'image/png'].includes(selectedFile.type)) {
        setError('File must be PDF, JPEG, or PNG');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const renderInstructions = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader 
        title="How to Get Your Food Handlers Certificate"
        sx={{ pb: 1 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 1: Complete the Online Course
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Complete the free online food safety course through the NYC Health Academy.
            </Typography>
            <Link 
              href="https://a816-healthpsi.nyc.gov/prjFPC/F2300_Default.aspx"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
            >
              Access the course
              <ExternalLink sx={{ ml: 0.5, width: 16, height: 16 }} />
            </Link>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Step 2: Schedule and Take the Exam
            </Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Exam fee: $24.60 (including fees)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Payment required: Credit card only
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Location: NYC Health Academy Riverside Health Center
                <br />160 West 100th Street, Third Floor, Manhattan
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                The exam fee will be reimbursed upon successful completion
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );


  const renderCertificate = () => {
    if (!imageUrl) return null;

    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        p: isMobile ? 1 : 2,
        mb: 2,
        overflow: 'auto'
      }}>
        <Box
          component="img"
          src={imageUrl}
          alt="Food Handler's Certificate"
          sx={{
            maxWidth: '100%',
            maxHeight: isMobile ? '400px' : '500px',
            width: 'auto',
            height: 'auto',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'block',
            objectFit: 'contain'
          }}
        />
      </Box>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!certificationChecked) {
      setError('Please certify that this is your valid Food Handlers Certificate');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('certificate', file);
      
      const uploadResponse = await fetch(`${API_URL}/employees/${employeeId}/food-handlers-cert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      const uploadData = await uploadResponse.json();
      const newCert = {
        path: uploadData.certificatePath,
        filename: uploadData.originalName,
        uploadedAt: new Date().toISOString()
      };
      
      setExistingCert(newCert);
      setUploadMode(false);
      
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        // Handle different path formats
        let cleanPath = uploadData.certificatePath;
        
        // Remove leading ./ if present
        cleanPath = cleanPath.replace(/^\.\//, '');
        
        // If path doesn't start with uploads/, assume it needs the full path
        if (!cleanPath.startsWith('uploads/')) {
          cleanPath = `uploads/food_handlers_certificate/${cleanPath}`;
        }
        
        // Remove 'uploads/' prefix since the API route already includes it
        const pathWithoutUploads = cleanPath.replace(/^uploads\//, '');
        // inline=1 streams the bytes from our own origin. Without it this route
        // 302s to an R2 presigned URL, and CSP connect-src is enforced on every
        // redirect hop, so the fetch threw "Failed to fetch" — after the upload
        // had already succeeded. The employee saw an error for a certificate we
        // had stored, which is precisely the direct-deposit complaint.
        const certUrl = `${API_URL}/uploads/${pathWithoutUploads}?inline=1`;

        // Preview only. It must never fail the upload it follows.
        try {
          const imageResponse = await fetch(certUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (imageResponse.ok) {
            const blob = await imageResponse.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageUrl(objectUrl);
          }
        } catch (previewErr) {
          console.warn('Certificate preview failed to load (upload itself succeeded):', previewErr);
        }
      }

      setSuccess(true);
      setIsCompletedState(true);
      setFile(null);
      setCertificationChecked(false);
      
      // Manually trigger a form status update to ensure it's marked as completed
      try {
        const statusResponse = await fetch(`${API_URL}/employee_forms/update-status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employeeId: employeeId,
            formId: 11, // Food Handlers Certificate form ID in onboarding_letters table
            isCompleted: true,
            documentStatus: 'Completed'
          })
        });
        
        if (!statusResponse.ok) {
          console.error('Failed to update form status');
        }
      } catch (statusErr) {
        console.error('Error updating form status:', statusErr);
      }
      
      // Pass both formId and the file path to trigger status update
      if (onSubmit) {
        await onSubmit(11, uploadData.certificatePath); // Use formId 11 for Food Handlers (from onboarding_letters table)
      }
      
      // Force refresh the page to show updated status
      // This is a temporary fix - ideally the parent component should refresh the forms list
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error('Error uploading certificate:', err);
      setError(err.message || 'Failed to upload certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCertificate = async () => {
    setRemoveLoading(true);
    try {
      const removeResponse = await fetch(`${API_URL}/employees/${employeeId}/food-handlers-cert`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove certificate');
      }

      const statusResponse = await fetch(`${API_URL}/employee_forms/update-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          employeeId,
          formId: 11, // Food Handlers Certificate form ID in onboarding_letters table
          isCompleted: false,
          documentStatus: 'Pending'
        })
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to update form status');
      }

      setExistingCert(null);
      setIsCompletedState(false);
      setImageUrl('');
      setSuccess(false);
      setUploadMode(false);
      
      // Notify parent component to refresh form status
      if (onSubmit) {
        await onSubmit(11, null); // Use formId 11 for Food Handlers (from onboarding_letters table)
      }
      
      // Force refresh the page to show updated status
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
    } catch (err) {
      console.error('Error removing certificate:', err);
      setError(err.message || 'Failed to remove certificate');
    } finally {
      setRemoveLoading(false);
    }
  };

  useEffect(() => {
    const fetchExistingCert = async () => {
      if (!employeeId) return;
      
      try {
        const response = await fetch(`${API_URL}/employees/${employeeId}/food-handlers-cert`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.certificatePath) {
            setExistingCert({
              path: data.certificatePath,
              uploadedAt: data.uploadedAt
            });
            setIsCompletedState(true);
            
            // Handle different path formats
            let cleanPath = data.certificatePath;
            
            // Remove leading ./ if present
            cleanPath = cleanPath.replace(/^\.\//, '');
            
            // If path doesn't start with uploads/, assume it needs the full path
            if (!cleanPath.startsWith('uploads/')) {
              cleanPath = `uploads/food_handlers_certificate/${cleanPath}`;
            }
            
            if (!cleanPath.toLowerCase().endsWith('.pdf')) {
              // Remove 'uploads/' prefix since the API route already includes it
              const pathWithoutUploads = cleanPath.replace(/^uploads\//, '');
              // inline=1: stream from our origin rather than redirecting to R2,
              // which CSP connect-src blocks on the redirect hop.
              const certUrl = `${API_URL}/uploads/${pathWithoutUploads}?inline=1`;

              const imageResponse = await fetch(certUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (!imageResponse.ok) {
                throw new Error(`Failed to load image: ${imageResponse.status}`);
              }

              const blob = await imageResponse.blob();
              const objectUrl = URL.createObjectURL(blob);
              setImageUrl(objectUrl);
            }
          } else {
            setIsCompletedState(false);
          }
        }
      } catch (err) {
        console.error('Error fetching/loading certificate:', err);
        setError('Failed to load certificate');
      }
    };

    fetchExistingCert();

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [employeeId, API_URL, token]);

  const renderRequirements = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Food Handlers Certificate Requirements
        </Typography>
        <Typography variant="body1" paragraph>
          A Food Handlers Certificate (also known as a Food Handler Card) is required for all employees 
          who handle, prepare, or serve food. This certification demonstrates your understanding of:
        </Typography>
        <ul>
          <li>Basic food safety principles</li>
          <li>Proper food handling procedures</li>
          <li>Personal hygiene requirements</li>
          <li>Cross-contamination prevention</li>
          <li>Temperature control</li>
        </ul>
        <Typography variant="body2" color="text.secondary">
          Your certificate must be current and issued by an accredited organization.
          The document should clearly show your name and the date of completion.
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {renderInstructions()}

      {isCompletedState && existingCert && !uploadMode ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Food Handlers Certificate has been successfully uploaded and verified.
          </Alert>
          
          <Card sx={{ overflow: 'visible' }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'flex-start', 
                mb: 3,
                gap: 2
              }}>
                <Box sx={{ mb: isMobile ? 2 : 0 }}>
                  <Typography variant="h6" gutterBottom>
                    Current Certificate
                  </Typography>
                  {existingCert.uploadedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Uploaded on: {new Date(existingCert.uploadedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexDirection: isMobile ? 'row' : 'row',
                  justifyContent: isMobile ? 'stretch' : 'flex-end',
                  '& > button': isMobile ? { flex: 1 } : {}
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setUploadMode(true)}
                    startIcon={!isMobile && <FileUp />}
                    size={isMobile ? "medium" : "small"}
                    fullWidth={isMobile}
                  >
                    Replace
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveCertificate}
                    disabled={removeLoading}
                    size={isMobile ? "medium" : "small"}
                    fullWidth={isMobile}
                  >
                    {removeLoading ? 'Removing...' : 'Remove'}
                  </Button>
                </Box>
              </Box>
              
              {renderCertificate()}
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {uploadMode ? 'Upload New Certificate' : 'Upload Your Certificate'}
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<FileUp />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Certificate File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </Button>
              
              {file && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Selected file: {file.name}
                </Typography>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={certificationChecked}
                    onChange={(e) => setCertificationChecked(e.target.checked)}
                  />
                }
                label="I certify that this is my valid Food Handlers Certificate"
                sx={{ mb: 2, display: 'block' }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                {uploadMode && (
                  <Button
                    variant="outlined"
                    onClick={() => setUploadMode(false)}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!file || loading || !certificationChecked}
                  sx={{ flex: 1 }}
                >
                  {loading ? 'Uploading...' : 'Upload Certificate'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FoodHandlersCertForm;